import { useState, useEffect, useRef, useCallback } from "react";
import toast from "react-hot-toast";
import { sendPartnerInvitation, addShowcasePartner, searchPartnerAgencies } from "../../services/api";
import {
  Modal,
  Segmented,
  SearchInput,
  PickList,
  PickRow,
  OrgAvatar,
  Field,
  FieldInput,
  Button,
  SearchIcon,
  ChevronRightIcon,
  ImagePlaceholderIcon,
} from "./v2";

// Add a partner — My Agency V2 rebuild, 1:1 from ARTBOARD-SPEC board 4b.
// One modal, two paths (segmented switch):
//   1) Search on TransferPitch → pick a registered agency by ID → send a
//      partnership invite (RC-1 fix: agency-ID based, no email, no broken
//      self-filter). Appears on both walls only after they accept.
//   2) Add external partner → manual logo/name/type/website, no consent flow.
// Empty search result is never a dead end: it offers "Add <query> as external
// partner" inline, prefilling the external form (4b state 2).
//
// PRESENTATION ONLY (Brief Rules 6/8): debounce (300 ms) + min-2-char
// gate, handlers, reset/close-guard and toasts are byte-identical to the
// previous version — only markup/styling changed.
// NOTE (Rule 10): the artboard draws "Type (optional)" as a select with a
// chevron; the platform value is free-entry text — the field stays free
// text styled per CMP-INPUT (see DECISIONS.md PARTNERS).

const useDebounced = (value, ms) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
};

// Search-match highlight (board 4b): <mark> bg #FFF1B8 r3 padding 0 1px.
const markStyle = { background: "#FFF1B8", borderRadius: 3, padding: "0 1px", color: "inherit" };
const Highlight = ({ text = "", query = "" }) => {
  const q = (query || "").trim();
  const idx = q ? (text || "").toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (idx === -1) return text || "";
  return (
    <>
      {text.slice(0, idx)}
      <mark style={markStyle}>{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
};

const PartnerInvitationModal = ({ isOpen, onClose, onSuccess }) => {
  const [tab, setTab] = useState("search"); // 'search' | 'external'
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounced(query, 300);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [ext, setExt] = useState({ name: "", type_label: "", website: "", logo: null });
  const [submitting, setSubmitting] = useState(false);
  const logoRef = useRef(null);

  const runSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await searchPartnerAgencies(q.trim());
      // Returns { success, data:[{agency_id,name,country,logo_url}] } — self +
      // already-linked excluded server-side (RC-1 fixed at the source).
      const list = res?.success ? res.data || [] : [];
      setResults(
        list.map((a) => ({
          id: a.agency_id ?? a.id,
          name: a.name,
          country: a.country,
          logo_url: a.logo_url || a.profile_picture_url || null,
        })),
      );
    } catch (e) {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && tab === "search") runSearch(debouncedQuery);
  }, [debouncedQuery, isOpen, tab, runSearch]);

  const reset = () => {
    setTab("search");
    setQuery("");
    setResults([]);
    setSelected(null);
    setExt({ name: "", type_label: "", website: "", logo: null });
  };

  const handleClose = () => {
    if (!submitting) {
      reset();
      onClose();
    }
  };

  const handleSendInvite = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      const res = await sendPartnerInvitation(selected.id);
      if (res.success) {
        toast.success("Partnership invite sent.");
        onSuccess?.();
        handleClose();
      } else {
        toast.error(typeof res.error === "string" ? res.error : "Failed to send invitation");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddExternal = async () => {
    if (!ext.name.trim()) {
      toast.error("Partner name is required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await addShowcasePartner({
        name: ext.name.trim(),
        type_label: ext.type_label.trim(),
        website: ext.website.trim(),
        logo: ext.logo,
      });
      if (res.success) {
        toast.success("Partner added.");
        onSuccess?.();
        handleClose();
      } else {
        toast.error(typeof res.error === "string" ? res.error : "Failed to add partner");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const goExternalWith = (prefillName) => {
    setExt((p) => ({ ...p, name: prefillName }));
    setTab("external");
  };

  if (!isOpen) return null;

  const trimmedQuery = query.trim();

  const footer = (primary) => (
    <div className="flex justify-end gap-2.5" style={{ marginTop: 16 }}>
      <Button variant="secondary" onClick={handleClose} disabled={submitting}>
        Cancel
      </Button>
      {primary}
    </div>
  );

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      width={400}
      padding={22}
      // Board 4b header title is 16px/600 (CMP-MODAL range 16–17).
      title={<span className="text-[16px]">Add a partner</span>}
      closeLabel="Close"
    >
      {/* Path switch (CMP-SEG modal variant, mt14) */}
      <Segmented
        modal
        activeKey={tab}
        onChange={setTab}
        items={[
          { key: "search", label: "Search on TransferPitch" },
          { key: "external", label: "Add external partner" },
        ]}
      />

      {tab === "search" ? (
        <>
          <div style={{ marginTop: 14 }}>
            <SearchInput
              width="100%"
              iconSize={14}
              clearIconSize={10}
              value={query}
              onChange={(v) => {
                setQuery(v);
                setSelected(null);
              }}
              onClear={() => {
                setQuery("");
                setSelected(null);
              }}
              clearTitle="Clear"
              placeholder="Search for an agency…"
              inputProps={{ autoFocus: true }}
            />
          </div>

          {searching ? (
            <div className="text-center py-5 text-[13px] text-[#64748B]">Searching…</div>
          ) : trimmedQuery.length >= 2 && results.length === 0 ? (
            /* Empty search result (4b state 2) — never a dead end. */
            <div
              className="flex flex-col items-center border border-[#E3E9F1]"
              style={{ padding: "26px 16px", borderRadius: 11, marginTop: 10 }}
            >
              <div className="w-10 h-10 rounded-full bg-[#F1F5F9] flex items-center justify-center">
                <SearchIcon size={17} />
              </div>
              <div className="text-[13.5px] font-semibold text-[#0F172A] text-center" style={{ marginTop: 11 }}>
                No agency matches “{trimmedQuery}”
              </div>
              <div className="text-[12.5px] text-[#64748B] mt-1">Not on TransferPitch?</div>
              <Button
                variant="tertiary"
                icon={null}
                onClick={() => goExternalWith(trimmedQuery)}
                className="inline-flex items-center gap-1.5"
                style={{ marginTop: 12 }}
              >
                Add “{trimmedQuery}” as external partner
                <ChevronRightIcon size={12} strokeWidth={2.4} stroke="#1668D9" />
              </Button>
            </div>
          ) : results.length > 0 ? (
            <PickList style={{ maxHeight: 220, overflowY: "auto" }}>
              {results.map((a, i) => (
                <PickRow
                  key={a.id}
                  first={i === 0}
                  selected={selected?.id === a.id}
                  onClick={() => setSelected(a)}
                  avatar={<OrgAvatar src={a.logo_url} name={a.name} seed={a.id ?? a.name} size={36} radius={10} />}
                  name={<Highlight text={a.name} query={trimmedQuery} />}
                  subline={
                    <span className="text-[11.5px] text-[#94A3B8]">
                      Agency{a.country ? ` · ${a.country}` : ""}
                    </span>
                  }
                />
              ))}
            </PickList>
          ) : (
            <div className="text-center py-5 text-[12.5px] text-[#94A3B8]">Type an agency name to search.</div>
          )}

          {/* Consent helper (4b state 1) */}
          <div className="text-[11.5px] text-[#94A3B8]" style={{ lineHeight: 1.5, marginTop: 10 }}>
            Appears on both walls once they accept.
          </div>

          {footer(
            <Button onClick={handleSendInvite} disabled={!selected || submitting}>
              {submitting ? "Sending…" : "Send partnership invite"}
            </Button>,
          )}
        </>
      ) : (
        <>
          {/* External partner form (4b state 3): logo tile 56 r14 dashed +
              Partner name*; then Type (optional) + Website (optional). */}
          <div className="flex items-center" style={{ gap: 14, marginTop: 16 }}>
            <button
              type="button"
              onClick={() => logoRef.current?.click()}
              className="w-14 h-14 rounded-[14px] border-[1.5px] border-dashed border-[#C4CEDA] flex flex-col items-center justify-center gap-0.5 flex-none bg-transparent cursor-pointer hover:border-[#2283FF] overflow-hidden"
            >
              {ext.logo ? (
                <img src={URL.createObjectURL(ext.logo)} alt="logo" className="w-full h-full object-cover" />
              ) : (
                <>
                  <ImagePlaceholderIcon size={16} />
                  <span className="text-[8.5px] font-mono text-[#94A3B8]">logo</span>
                </>
              )}
            </button>
            <input
              ref={logoRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => setExt((p) => ({ ...p, logo: e.target.files?.[0] || null }))}
            />
            <Field label="Partner name" required style={{ flex: 1 }}>
              <FieldInput
                value={ext.name}
                onChange={(e) => setExt((p) => ({ ...p, name: e.target.value }))}
                placeholder="Riverton Academy"
              />
            </Field>
          </div>

          <div className="flex" style={{ gap: 10, marginTop: 12 }}>
            <Field label="Type" optionalLabel="optional" style={{ flex: 1 }}>
              <FieldInput
                value={ext.type_label}
                onChange={(e) => setExt((p) => ({ ...p, type_label: e.target.value }))}
                placeholder="Academy"
              />
            </Field>
            <Field label="Website" optionalLabel="optional" style={{ flex: 1 }}>
              <FieldInput
                value={ext.website}
                onChange={(e) => setExt((p) => ({ ...p, website: e.target.value }))}
                placeholder="https://…"
              />
            </Field>
          </div>

          {/* No-consent helper (4b state 3) */}
          <div className="text-[11.5px] text-[#94A3B8]" style={{ lineHeight: 1.5, marginTop: 10 }}>
            No consent flow. Shown only on your wall. Remove anytime.
          </div>

          {footer(
            <Button onClick={handleAddExternal} disabled={submitting || !ext.name.trim()}>
              {submitting ? "Adding…" : "Add partner"}
            </Button>,
          )}
        </>
      )}
    </Modal>
  );
};

export default PartnerInvitationModal;
